import { useResidentContext } from "app/features/residents/provider/ResidentProvider";
import { useGetResidentUrgentConcernsIndexQuery } from "app/services/api/urgentConcernsApi";
import { UrgentConcernStatusEnum } from "app/services/api/types/shiftHandover/enums/UrgentConcernStatusEnum";
import { RootState } from "app/services/store";
import { selectResidentUrgentConcerns } from "app/store/slices/urgentConcerns";
import { selectIsOnline } from "app/store/slices/network";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import usePagination from "ui/molecules/pagination/usePagination";

interface Options {
  status?: UrgentConcernStatusEnum;
}

export function useUrgentConcernsWithOffline({ status }: Options = {}) {
  const { residentId } = useResidentContext();
  const { currentPage, handlePageChange } = usePagination();

  const isOnline = useSelector(selectIsOnline);
  const offlineData = useSelector((state: RootState) =>
    selectResidentUrgentConcerns(state, residentId),
  );

  const {
    data: onlineData,
    isFetching,
    isLoading,
    isError,
  } = useGetResidentUrgentConcernsIndexQuery(
    { residentId: Number(residentId), page: currentPage, filter: { status } },
    { skip: !residentId },
  );

  const data = useMemo(() => {
    if (isFetching || (isOnline && onlineData)) return onlineData?.data;
    if (isError && offlineData) return offlineData;
    return onlineData?.data;
  }, [onlineData, offlineData, isOnline, isFetching, isError]);

  const metaData = useMemo(() => onlineData?.meta, [onlineData]);

  return {
    data,
    isFetching,
    isLoading,
    meta: metaData,
    handlePageChange,
  };
}
